/**
 * Downscale an image for AI (vision) requests.
 *
 * Vision models work at ~1-2k px — sending a full-resolution photo only slows
 * the request down (a 5 MB upload becomes ~150 KB after downscaling, and the
 * payload otherwise travels browser → backend → Azure OpenAI per model call).
 * The ORIGINAL file is untouched; this only shrinks the copy sent to the AI.
 *
 * Accepts a File/Blob or any URL the browser can fetch (object URL, data URL,
 * same-origin material URL). Returns a JPEG data URL fitting `maxDim`, drawn
 * on a white backing so transparent PNGs don't turn black. Falls back to the
 * un-scaled data URL if canvas processing fails (e.g. an exotic format).
 */
const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export default async function downscaleImageToDataUrl(source, maxDim = 1024, quality = 0.85) {
  const blob = source instanceof Blob ? source : await (await fetch(source)).blob();
  const dataUrl = await blobToDataUrl(blob);

  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });

    const scale = Math.min(1, maxDim / Math.max(img.width || 1, img.height || 1));
    // Already small enough — skip the canvas round-trip.
    if (scale >= 1 && blob.size < 400000) return dataUrl;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    return dataUrl;
  }
}
